import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Copy, ArrowLeft, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

function daysLeft(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  const now = Date.now();
  if (t < now) return 0;
  return Math.ceil((t - now) / (1000 * 60 * 60 * 24));
}

export default function Referrals() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/referrals/me");
        setStats(data);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, authLoading, navigate]);

  const shareUrl = user ? `${window.location.origin}/?ref=${encodeURIComponent(user.id)}` : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied. Share it anywhere.");
    } catch (e) {
      toast.error("Couldn't copy — long-press to share.");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="referrals-page">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 hover:text-[#E63946]"
        data-testid="referrals-back-link"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={2.5} />
        Back to home
      </Link>

      <div className="mt-6 mb-10 border-b border-black/10 pb-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-2 inline-flex items-center gap-1.5">
          <Gift className="w-3 h-3" strokeWidth={2.5} />
          Referral Rewards
        </div>
        <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter">
          Bring a friend, earn Pro.
        </h1>
        <p className="mt-3 text-black/60 font-serif italic max-w-2xl">
          Share your DealHub link. When a friend signs up, you get <span className="text-[#E63946] font-bold not-italic">30 days of DealHub Pro</span> — stacking for every referral. No cap.
        </p>
      </div>

      {loading ? (
        <div className="text-xs font-mono uppercase tracking-widest text-black/40">Loading…</div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="border-2 border-black bg-white p-6" data-testid="stat-referrals">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">Friends referred</div>
              <div className="font-serif font-black text-5xl tracking-tighter">{stats?.referral_count || 0}</div>
            </div>
            <div className="border-2 border-black bg-white p-6" data-testid="stat-credits">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">Pro credits earned</div>
              <div className="font-serif font-black text-5xl tracking-tighter">{stats?.credits_earned || 0}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-black/40 mt-1">
                {(stats?.reward_days_per_referral || 30)} days each
              </div>
            </div>
            <div className={`border-2 p-6 ${stats?.is_pro_active ? "border-[#E63946] bg-[#FFF5F6]" : "border-black/20 bg-white"}`} data-testid="stat-pro-status">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-2">Pro status</div>
              <div className="font-serif font-black text-2xl md:text-3xl tracking-tight inline-flex items-center gap-2">
                {stats?.is_pro_active ? (<><Sparkles className="w-6 h-6 text-[#E63946]" strokeWidth={1.5} /> Active</>) : "Not yet"}
              </div>
              {stats?.pro_credit_expires_at && (
                <div className="text-[10px] font-mono uppercase tracking-widest text-black/60 mt-1" data-testid="pro-credit-expiry">
                  {daysLeft(stats.pro_credit_expires_at) > 0
                    ? `${daysLeft(stats.pro_credit_expires_at)} days remaining`
                    : "Expired"}
                </div>
              )}
            </div>
          </div>

          {/* Share link */}
          <div className="border-2 border-black bg-[#FAFAFA] p-6 mb-10">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3">Your share link</div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 border border-black/20 bg-white px-4 py-3 font-mono text-xs"
                data-testid="referral-share-input"
              />
              <button
                onClick={copy}
                className="inline-flex items-center justify-center gap-2 bg-[#111111] text-white text-xs font-bold uppercase tracking-[0.25em] px-5 py-3 hover:bg-[#E63946]"
                data-testid="referral-copy-btn"
              >
                <Copy className="w-4 h-4" strokeWidth={2} />
                Copy link
              </button>
            </div>
            <p className="text-[11px] font-mono uppercase tracking-widest text-black/40 mt-3">
              Tip: every deal page also carries your ref when you click Copy Link.
            </p>
          </div>

          {/* Recent referrals */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-black/50 mb-3">Recent activity</div>
            {stats?.recent?.length ? (
              <ul className="border border-black/10 bg-white" data-testid="referral-activity-list">
                {stats.recent.map((r, i) => (
                  <li key={r.id || `${r.referee_email}-${r.created_at}` || i} className="px-4 py-3 border-b border-black/5 last:border-0 flex items-center justify-between text-sm" data-testid={`referral-item-${i}`}>
                    <div className="font-serif">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#E63946] mr-2">✓ Signed up</span>
                      {r.referee_email}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-black/40">
                      {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="border border-dashed border-black/20 p-8 text-center text-black/50 font-serif italic" data-testid="referral-activity-empty">
                Nobody has signed up through your link yet — share it and check back.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
